import { useEffect, useRef, useState } from "react";
import Popover from "../popover/popover";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import useOnClickOutside from "../../../../../helpers/hooks/useOnClickOutside";
import { setPersonalMarkersPopover } from "../../../../../redux/slices/popoversSlice";
import { conditions, handleHasXTokenParamOrSendXToken, handleVideoType, responseValidator, videoTag } from "../../../../../helpers";
import { ConditionName, IMarker } from "../../../../../interfaces";
import { API, apiCaller } from "../../../../../services";
import { combinedMarkers } from "../../../../../redux/actions/videoData.actions";
import { setSelectedPersonalMarker } from "../../../../../redux/slices/userSlice";
import PersonalMarkerIcon from "../../icon-components/personalMarkerIcon";
import { setMarkers } from "../../../../../redux/slices/videoDataSlice";
import EmojiPicker from "../emojiPicker/emojiPicker";
import { shallowEqual } from "react-redux";
import { toast } from "mobin-toast";
import dynamic from "next/dynamic";

const Emoji = dynamic(() => import("../emoji/emoji"));
const PlusEmojiIcon = dynamic(() => import("../../icon-components/plusEmojiIcon"));

const PersonalMarkersPopover = () => {
    const [showEmojiPickerPopover, setShowEmojiPickerPopover] = useState<boolean>(false);
    const [selectedEmoji, setSelectedEmoji] = useState<string>("");
    const [markerName, setMarkerName] = useState<string>("");
    const [markerDescription, setMarkerDescription] = useState<string>("");
    const [isFocus, setIsFocus] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { personalMarkersPopover } = useAppSelector(({ popovers: { personalMarkersPopover } }) => ({ personalMarkersPopover }), shallowEqual);
    const { params, details, markers } = useAppSelector(
        ({ videoData: { params, details, markers, isComplexDesktop, isComplexPortrait } }) => ({
            params,
            details,
            markers,
            isComplexDesktop,
            isComplexPortrait
        }),
        shallowEqual
    );
    const { selectedPersonalMarker } = useAppSelector(
        ({ user: { selectedPersonalMarker, isLogin } }) => ({ selectedPersonalMarker, isLogin }),
        shallowEqual
    );

    const PersonalPopRef = useRef(null);

    useOnClickOutside(PersonalPopRef, () => {
        if (personalMarkersPopover) dispatch(setPersonalMarkersPopover(true));
    });

    useEffect(() => {
        if (!!selectedPersonalMarker) {
            setSelectedEmoji(selectedPersonalMarker.sticker);
            setMarkerName(selectedPersonalMarker.title);
            setMarkerDescription(selectedPersonalMarker.description);
        }
    }, [selectedPersonalMarker]);

    useEffect(() => {
        if (conditions(ConditionName.isTouchScreen)) setShowEmojiPickerPopover(false);
    }, [selectedEmoji]);

    const handlePostPersonalMarkers = async () => {
        if (!selectedEmoji) toast({ title: "لطفا یک استیکر انتخاب نمایید.", type: "error" });
        else if (!markerName) toast({ title: "لطفا نام نشانگر را وارد کنید.", type: "error" });
        else if (markerName.length > 255)
            toast({
                title: "اندازه نام نشانگر نباید بیش از 256 حرف باشد.",
                type: "error"
            });
        else if (!!selectedPersonalMarker) {
            if (conditions(ConditionName.trackerEnabled)) {
                const { status, data } = await apiCaller({
                    url: API().user.updatePersonalMarker(selectedPersonalMarker.isPersonal!),
                    method: "PATCH",
                    data: {
                        description: markerDescription,
                        name: markerName,
                        sticker: selectedEmoji,
                        time: selectedPersonalMarker.time
                    },
                    headers: handleHasXTokenParamOrSendXToken()
                });
                if (responseValidator(status)) {
                    toast({ title: "نشانگر با موفقیت ویرایش شد.", type: "success" });
                    dispatch(
                        setMarkers(
                            markers.map((m: IMarker) => {
                                if (!!m.isPersonal && m.isPersonal === selectedPersonalMarker.isPersonal)
                                    return {
                                        ...m,
                                        description: markerDescription,
                                        sticker: selectedEmoji,
                                        title: markerName
                                    };
                                else return m;
                            })
                        )
                    );
                } else toast({ type: "error", title: data.detail });
            }
            handleClosePersonalMarkersPopover();
        } else if (!markers.find((m: IMarker) => m.time === Math.ceil(videoTag().currentTime))) {
            if (conditions(ConditionName.trackerEnabled)) {
                const marker: any = {
                    video_type: handleVideoType(),
                    video_id: conditions(ConditionName.isLiveParam) ? details.id : Number(params.id),
                    name: markerName,
                    time: Math.ceil(videoTag().currentTime),
                    sticker: selectedEmoji,
                    description: markerDescription
                };
                const { data, status } = await apiCaller({
                    url: API().user.createPersonalMarker,
                    method: "POST",
                    data: marker,
                    headers: handleHasXTokenParamOrSendXToken()
                });
                if (responseValidator(status)) {
                    toast({ title: "نشانگر با موفقیت اضافه شد.", type: "success" });
                    combinedMarkers([data], markers);
                } else toast({ type: "error", title: data.detail });
            }
            handleClosePersonalMarkersPopover();
        } else toast({ title: "خطا در ثبت دو نشانگر همزمان", type: "error" });
    };

    const handleClosePersonalMarkersPopover = () => {
        if (selectedPersonalMarker) dispatch(setSelectedPersonalMarker(undefined));
        dispatch(setPersonalMarkersPopover(false));
        setSelectedEmoji("");
        setMarkerName("");
        setMarkerDescription("");
    };

    return (
        <>
            <div
                className="pl-flex pl-items-center pl-gap-[14px] pl-rounded-[20px] pl-text-secondary-700 dark:pl-text-secondary-50 pl-bg-gray-100 dark:pl-bg-dark-3 pl-px-4 pl-py-[7px] pl-cursor-pointer"
                onClick={() => {
                    if (conditions(ConditionName.hasXToken)) dispatch(setPersonalMarkersPopover(true));
                    else toast({ title: "لطفا ابتدا وارد شوید.", type: "error" });
                }}
            >
                <PersonalMarkerIcon
                    className={`pl-fill-z-gray-500 dark:pl-fill-dark-5 ${
                        conditions(ConditionName.isComplexPortrait) ? "pl-w-4 pl-h-4" : "pl-w-[22px] pl-h-[22px]"
                    }`}
                />
                <p className="pl-whitespace-nowrap">افزودن نشانگر شخصی</p>
            </div>
            <Popover
                classNames={
                    conditions(ConditionName.isComplexDesktop) && !conditions(ConditionName.isTouchScreenLandscape)
                        ? "!pl-w-[639px] pl-relative !pl-bg-white dark:!pl-bg-dark-3"
                        : conditions(ConditionName.isTouchScreenLandscape)
                        ? "!pl-w-[70vw] !pl-max-h-[90vh] pl-relative !pl-bg-white dark:!pl-bg-black"
                        : `!pl-w-full pl-max-h-[90vh] pl-relative !pl-bg-white dark:!pl-bg-black`
                }
                status={personalMarkersPopover || !!selectedPersonalMarker}
                isBlurBackground={conditions(ConditionName.isComplexDesktop)}
                ref={PersonalPopRef}
                isHasCatchUpLineOnTop={conditions(ConditionName.isComplexPortrait)}
                onClose={handleClosePersonalMarkersPopover}
                headerTitle={!!selectedPersonalMarker ? "ویرایش نشانگر شخصی" : "افزودن نشانگر شخصی"}
                isFullHeight={conditions(ConditionName.isComplexPortrait)}
                isHeader={true}
                isFocusOnInput={isFocus}
                type={"modal"}
            >
                <div>
                    <div className="pl-flex pl-flex-col pl-gap-4 pl-py-4 pl-w-full pl-justify-center pl-p-4">
                        <div
                            className="pl-relative pl-flex pl-justify-center pl-items-center pl-self-center pl-shrink-0 pl-border-[1.5px] pl-rounded-[12px] pl-bg-main/[0.1] pl-w-[126px] pl-h-[110px] pl-cursor-pointer"
                            onClick={() => setShowEmojiPickerPopover(true)}
                        >
                            {!!selectedEmoji ? (
                                <span className="pl-emoji-mart-emoji pl-flex pl-flex-col pl-items-center pl-gap-[11px] pl-justify-center pl-h-full">
                                    <Emoji name={selectedEmoji} className="pl-w-[56px] pl-h-[56px] pl-mx-auto" />
                                </span>
                            ) : (
                                <div className="pl-flex pl-flex-col pl-items-center pl-gap-[11px] pl-justify-center">
                                    <PlusEmojiIcon className={"pl-fill-main-light dark:pl-fill-z-50"} />
                                    <div className="pl-text-main-light dark:pl-text-z-50">افزودن استیکر</div>
                                </div>
                            )}
                        </div>
                        <div className="pl-w-full">
                            <label className={`pl-mb-2 pl-block pl-text-[14px]`}>
                                نام نشانگر<span className="pl-text-red-500">*</span>
                            </label>
                            <input
                                onFocus={() => setIsFocus(true)}
                                onBlur={() => setIsFocus(false)}
                                placeholder="نام نشانگر را وارد کنید."
                                value={markerName}
                                className="pl-border-[1.5px] pl-h-[48px] pl-p-4 pl-rounded-[10px] pl-w-full"
                                onChange={(e) => setMarkerName(e.target.value)}
                            />
                        </div>
                        <div className="w-full">
                            <label className={`pl-mb-2 pl-block pl-text-[14px]`}>توضیحات</label>
                            <input
                                onFocus={() => setIsFocus(true)}
                                onBlur={() => setIsFocus(false)}
                                placeholder="توضیحات مربوط به نشانگر را وارد کنید."
                                value={markerDescription}
                                className="pl-border-[1.5px] pl-h-[48px] pl-p-4 pl-rounded-[10px] pl-w-full"
                                onChange={(e) => setMarkerDescription(e.target.value)}
                            />
                        </div>
                        <div className="pl-flex pl-items-center pl-justify-center pl-gap-6">
                            {conditions(ConditionName.isComplexDesktop) && (
                                <button
                                    type="button"
                                    className={`pl-mt-2 pl-py-2 pl-px-6 pl-rounded-[8px] ${
                                        conditions(ConditionName.isComplexDesktop) ? "pl-w-1/3" : "pl-w-full"
                                    } pl-text-[15px] pl-font-medium pl-cursor-pointer pl-transition-colors pl-duration-300 pl-bg-transparent pl-text-main pl-border p-border-2 pl-border-main`}
                                    onClick={handleClosePersonalMarkersPopover}
                                >
                                    انصراف
                                </button>
                            )}
                            <button
                                type="button"
                                className={`pl-mt-2 pl-py-2 pl-px-6 !pl-bg-main-light ${
                                    conditions(ConditionName.isComplexDesktop) ? "pl-w-2/3" : "pl-w-full"
                                } pl-rounded-[8px] pl-text-[15px] pl-font-medium pl-cursor-pointer pl-transition-colors pl-duration-300 pl-text-white pl-border p-border-2 pl-border-main`}
                                onClick={handlePostPersonalMarkers}
                            >
                                {!!selectedPersonalMarker ? "ویرایش نشانگر شخصی" : "افزودن نشانگر شخصی"}
                            </button>
                        </div>
                    </div>
                    {conditions(ConditionName.isTouchScreen) && (
                        <div>
                            {showEmojiPickerPopover && (
                                <EmojiPicker
                                    enableUnified={true}
                                    setSelectedEmoji={setSelectedEmoji}
                                    onClickOutside={() => setShowEmojiPickerPopover(false)}
                                    emojiClassName={conditions(ConditionName.isTouchScreenLandscape) ? ["!pl-w-[70vw]", "!pl-h-[70vh]"] : []}
                                    parentClassName={`!pl-bottom-0`}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Popover>
            {conditions(ConditionName.isComplexDesktop) && (
                <Popover
                    classNames={"pl-fixed"}
                    status={showEmojiPickerPopover}
                    isBlurBackground={false}
                    ref={PersonalPopRef}
                    isHasCatchUpLineOnTop={false}
                    onClose={() => setShowEmojiPickerPopover(false)}
                    type={"modal"}
                >
                    <EmojiPicker
                        enableUnified={true}
                        setSelectedEmoji={setSelectedEmoji}
                        onClickOutside={() => setShowEmojiPickerPopover(false)}
                        emojiClassName={["pl-fixed", "pl-top-1/2", "pl-left-1/2", "pl-h-[300px]", "pl-w-[400px]", "-pl-translate-x-1/2"]}
                    />
                </Popover>
            )}
        </>
    );
};

export default PersonalMarkersPopover;
